import { useEffect } from 'react'

import { CoreComponentFormProps, CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'
import { getSafely } from '@/shared/safely'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { parseBooleanFromValue } from '../../../utils'
import { CompCheckboxProps } from './comp'

export interface CompCheckboxWrapperProps {
  infos: CoreComponentInfos
  form: CoreComponentFormProps
  children: (props: CompCheckboxProps) => React.ReactNode
}

/**
 * CompCheckboxWrapperCore
 */
export function CompCheckboxWrapperCore({
  infos: { settings, component, containerClient, detailTableId, detailRowIndex },
  form,
  children,
}: CompCheckboxWrapperProps) {
  const { openComponentSettings, isDevelopmentMode } = useAppBuilderContext()

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  // ========================= form settings =========================
  const { formState, setFormState, setRequiredFieldStatus } = form
  const columnId = settings[ComponentSettingKey.CheckboxDataValue] as string

  // ========================= data =========================
  const defaultValue =
    useParseVariableSelect(
      settings[ComponentSettingKey.CheckboxDataDefaultValue],
      containerClient,
      detailTableId,
      detailRowIndex
    ) ?? false

  const defaultValueBoolean = parseBooleanFromValue(defaultValue)

  const labelData = settings?.[ComponentSettingKey.CheckboxDataLabel]
  const label = useParseVariableText(labelData, containerClient, detailTableId, detailRowIndex)

  const isEditable = getSafely(settings, [ComponentSettingKey.CheckboxDataDefaultValueEditable], true)

  // ========================= behavior =========================
  const required = settings[ComponentSettingKey.CheckboxBehaviorRequired] as boolean

  // ========================= form state =========================

  const checked = typeof formState[columnId] !== 'undefined' ? Boolean(formState[columnId]) : defaultValueBoolean
  const handleChange = (checked: boolean) => {
    if (!columnId) return

    setFormState?.({
      ...(formState || {}),
      [columnId]: checked,
    })
  }

  const checkboxProps: CompCheckboxProps = {
    id: component._id,
    label,
    checked,
    onChange: handleChange as any,
    required,
    disabled: !isEditable,
  }

  // ========================= required validation =========================
  useEffect(() => {
    if (required) {
      setRequiredFieldStatus(columnId, !checked)
    }
    return () => {
      if (required) {
        setRequiredFieldStatus(columnId, false)
      }
    }
  }, [required, checked, columnId, setRequiredFieldStatus])

  if (isDevelopmentMode) {
    return (
      <div className='relative inline-block w-full border border-dashed' style={{ borderColor: '#333' }}>
        {children(checkboxProps)}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return children(checkboxProps)
}
